import {
  Field,
  Label,
} from '@headlessui/react'
import { useState, useEffect } from 'react'
import { IOption } from '../../types'
import { TagPill } from '../TagPill'
import { OkDialog } from '../dialogs/OkDialog'
import { SearchComboBoxField, CUSTOM_OPTION_ID } from './SearchComboBoxField'

interface ITagComboBoxFieldProps {
  label: string
  placeholder: string
  options: IOption[]
  value?: IOption[]
  onChange?: (value: IOption[]) => void
  error?: boolean
  maxLength?: number
}

const MAX_TAGS = 10

export const TagComboBoxField = (props: ITagComboBoxFieldProps) => {
  const [options, setOptions] = useState(props.options)
  const [showMaxTagsDialog, setShowMaxTagsDialog] = useState(false)

  const selectedTags = props.value ?? []

  // Update options when props.options changes (e.g., when notes are loaded)
  useEffect(() => {
    setOptions(props.options)
  }, [props.options])

  // Helper function to normalize strings for comparison
  const normalizeString = (str: string) => {
    return str.toLowerCase().replace(/\s+/g, '')
  }

  const onSelect = (value: IOption | null) => {
    if (value != null) {
      // Check if tag already exists in selected tags
      const exists = selectedTags.some((tag) => tag.id === value.id)
      if (!exists) {
        // Check if adding this tag would exceed the maximum
        if (selectedTags.length >= MAX_TAGS) {
          setShowMaxTagsDialog(true)
          return
        }

        // If it's a newly created option, check if it matches an existing option
        if (value.id === CUSTOM_OPTION_ID) {
          const normalizedInput = normalizeString(value.label)

          const existingSelectedTag = selectedTags.find(tag =>
            normalizeString(tag.label) === normalizedInput
          )

          if (existingSelectedTag) {
            return
          }

          const existingOption = options.find(option =>
            normalizeString(option.label) === normalizedInput
          )

          if (existingOption) {
            // If a match is found, use the existing option instead
            props.onChange?.([...selectedTags, existingOption])
          } else {
            // Otherwise, create a new option
            const newOption = { ...value, id: Date.now().toString() }
            props.onChange?.([...selectedTags, newOption])
          }
        } else {
          props.onChange?.([...selectedTags, value])
        }
      }
    }
  }

  const removeTag = (tagId: string) => {
    props.onChange?.(selectedTags.filter((tag) => tag.id !== tagId))
  }

  return (
    <>
      <Field>
        <Label className={'text-lg'}>{props.label}</Label>

        {/* Tags display */}
        {selectedTags.length > 0 && (
          <div className={'flex flex-wrap gap-2 mt-3'}>
            {selectedTags.map((tag) => (
              <TagPill
                key={tag.id}
                label={tag.label}
                imageUrl={tag.imageUrl}
                onRemove={() => removeTag(tag.id)}
              />
            ))}
          </div>
        )}

        <SearchComboBoxField
          placeholder={props.placeholder}
          options={options}
          value={null}
          onSelect={onSelect}
          error={props.error}
          maxLength={props.maxLength}
          resetAfterSelect={true}
        />
      </Field>

      <OkDialog
        isOpen={showMaxTagsDialog}
        onClose={() => setShowMaxTagsDialog(false)}
        title="Maximum Tags Reached"
        content={`You can only add up to ${MAX_TAGS} tags. Please remove a tag before adding a new one.`}
      />
    </>
  )
}
